import * as userFunctions from '../misc/UserFunctions.ts';

interface ErrorResponse {
    status: string;
    message: string;
    exception: string;
}

let fetchedApiUrl: string | null = null;
let env: string | null = null;
export const ENV_PRD = "prd";

export class UserErrorMessage extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'UserErrorMessage';

        const errorConstructor = Error as any;
        if (errorConstructor.captureStackTrace) {
            errorConstructor.captureStackTrace(this, UserErrorMessage);
        }
    }
}

const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const determineConfig = (): void => {
    const url = window.location.href;

    const configMap: Record<string, { apiUrl: string; env: string }> = {
        "localhost": { apiUrl: "http://127.0.0.1:3333/", env: "local" },
        "dev.fungres.org": { apiUrl: "https://api-dev.fungres.org/", env: "dev" },
        "test.fungres.org": { apiUrl: "https://api-test.fungres.org/", env: "test" },
        "fungres.org": { apiUrl: "https://api.fungres.org/", env: "prd" }
    };

    for (const key in configMap) {
        if (url.includes(key)) {
            fetchedApiUrl = configMap[key].apiUrl;
            env = configMap[key].env;
            return;
        }
    }
    console.log("Unknown environment: " + url);
};

export const getBaseApiUrl = (): string | null => {
    if (!fetchedApiUrl) determineConfig();
    return fetchedApiUrl;
};

export const jsonToUrlEncoded = (data: any, prefix = ""): string => {
    return Object.keys(data).map(key => {
        const fullKey = prefix ? `${prefix}[${key}]` : key;
        return typeof data[key] === 'object'
            ? jsonToUrlEncoded(data[key], fullKey)
            : `${encodeURIComponent(fullKey)}=${encodeURIComponent(data[key])}`;
    }).join('&');
};

const getAuthHeaders = (): Record<string, string> => {
    const token = userFunctions.getTokenOrRedirect();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

const fetchData = async (url: string, options: any, redirect: boolean = false): Promise<any> => {
    try {
        const response = await fetch(url, options);

        if (redirect && response.status === 401) {
            userFunctions.redirectToLogon();
            return;
        }

        if (!redirect && response.status === 401) {
            throw new UserErrorMessage("Password/username is incorrect.");
        }

        if (!response.ok) {
            const errorResponse: ErrorResponse | null = await tryGetErrorResponse(response);
            throw new UserErrorMessage(errorResponse?.message || response.statusText);
        }

        return await response.json();
    } catch (error) {
        console.error('Error during fetch:', error);
        throw error;
    }
};

const tryGetErrorResponse = async (response: Response): Promise<ErrorResponse | null> => {
    try {
        return await response.json();
    } catch {
        return null;
    }
};

export const getSimpleData = async (relativeUrl: string): Promise<any> => {
    const baseUrl = getBaseApiUrl();
    return fetchData(`${baseUrl}${relativeUrl}`, { method: 'GET', headers: getAuthHeaders() });
};

export const putSimple = async (relativeUrl: string, data: any): Promise<any> => {
    const baseUrl = getBaseApiUrl();
    return fetchData(`${baseUrl}${relativeUrl}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
};

export const postSimple = async (relativeUrl: string, data: any): Promise<any> => {
    const baseUrl = getBaseApiUrl();
    return fetchData(`${baseUrl}${relativeUrl}`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
};

export const deleteSimple = async (relativeUrl: string): Promise<any> => {
    const baseUrl = getBaseApiUrl();
    return fetchData(`${baseUrl}${relativeUrl}`, { method: 'DELETE', headers: getAuthHeaders() });
};

export interface User {
    email: string;
    full_name: string;
    role: string;
    access_token: string;
}

export const getUserAndToken = async (username: string, password: string): Promise<User> => {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}get_token_and_user`;

    return fetchData(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: jsonToUrlEncoded({ username, password })
    }, false);
};

export const getFile = async (relativeUrl: string): Promise<Blob | null> => {
    const baseUrl = getBaseApiUrl();
    const response = await fetch(`${baseUrl}${relativeUrl}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${userFunctions.getTokenOrRedirect()}` }
    });

    if (!response.ok) {
        let errorMessage = `Download failed: ${response.statusText}`;
        try {
            const errorData = await response.json();
            if (errorData.detail) {
                errorMessage = errorData.detail;
            }
        } catch (e) {
            // If we can't parse JSON, use the default error message
        }
        throw new Error(errorMessage);
    }

    return response.blob();
};

export const uploadFile = async (relativeUrl: string, file: File): Promise<Blob | string | null> => {
    const baseUrl = getBaseApiUrl();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${baseUrl}${relativeUrl}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${userFunctions.getTokenOrRedirect()}` },
        body: formData
    });

    if (response.status === 400) {
        return response.headers.get('content-type')?.includes('application/json')
            ? (await response.json()).message
            : await response.blob();
    }

    return response.ok ? null : Promise.reject(new Error(`Upload failed: ${response.statusText}`));
};

export const uploadMultipleFiles = async (relativeUrl: string, files: File[]): Promise<any> => {
    const baseUrl = getBaseApiUrl();
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));

    const response = await fetch(`${baseUrl}${relativeUrl}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${userFunctions.getTokenOrRedirect()}` },
        body: formData
    });

    if (!response.ok) {
        let errorMessage = `Upload failed: ${response.statusText}`;
        try {
            const errorData = await response.json();
            if (errorData.detail) {
                errorMessage = errorData.detail;
            }
        } catch (e) {
            // If we can't parse JSON, use the default error message
        }
        throw new Error(errorMessage);
    }

    return await response.json();
};
